import { useEffect, useRef, useState } from 'react'

// Shared by BaseDashboard's header RefreshControl and ResizableTable's own
// "..." menu — both need the exact same "updated Xm ago" ticker + auto-
// refresh timer, just rendered in different chrome (a roomy header cluster
// vs. a compact table control-bar popover).
export function formatRelativeUpdated(loadedAt: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - loadedAt) / 1000))
  if (seconds < 45) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

export const AUTO_REFRESH_OPTIONS: { label: string; ms: number | null }[] = [
  { label: 'Auto off', ms: null },
  { label: 'Auto 1 min', ms: 60_000 },
  { label: 'Auto 3 min', ms: 3 * 60_000 },
  { label: 'Auto 5 min', ms: 5 * 60_000 },
  { label: 'Auto 10 min', ms: 10 * 60_000 },
]

// localStorage-backed persistence, namespaced per caller (storageKey) since
// "how often should this refresh" is a per-dashboard/per-table choice, not
// one global preference.
const STORAGE_PREFIX = 'trustfence-backoffice-auto-refresh:'

function readStoredIntervalMs(storageKey?: string): number | null {
  if (!storageKey || typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey)
  if (!raw) return null
  const ms = Number(raw)
  // Only trust a value that's still one of today's selectable options —
  // guards against a stale/tampered entry (or a removed option) silently
  // driving an interval no UI actually offers.
  return AUTO_REFRESH_OPTIONS.some((opt) => opt.ms === ms) ? ms : null
}

// Owns the two timers a "last updated + auto-refresh" control needs: a slow
// tick to keep the relative-time label advancing, and (when enabled) a
// periodic call to the caller's onRefresh. Interval state lives here rather
// than being lifted to the caller, but storageKey (when passed) makes the
// selected interval survive a reload instead of always starting back at
// "Auto off".
export function useAutoRefresh({ lastUpdated, onRefresh, storageKey }: { lastUpdated?: number | null; onRefresh?: () => void; storageKey?: string }) {
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [intervalMs, setIntervalMsState] = useState<number | null>(() => readStoredIntervalMs(storageKey))
  // Latest onRefresh in a ref so the interval effect below doesn't need to
  // restart (and skip a beat) every time the caller passes a new closure.
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  const setIntervalMs = (ms: number | null) => {
    setIntervalMsState(ms)
    if (!storageKey) return
    if (ms == null) window.localStorage.removeItem(STORAGE_PREFIX + storageKey)
    else window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(ms))
  }

  useEffect(() => {
    if (lastUpdated == null) return
    const timer = setInterval(() => setNowTick(Date.now()), 15_000)
    return () => clearInterval(timer)
  }, [lastUpdated != null])

  useEffect(() => {
    if (!intervalMs) return
    const timer = setInterval(() => onRefreshRef.current?.(), intervalMs)
    return () => clearInterval(timer)
  }, [intervalMs])

  return { nowTick, intervalMs, setIntervalMs }
}
